import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Action } from '../../../models/core/action';
import { FormatClaimPipe } from '../../../pipes/format-claim.pipe';
import { ExportService } from '../../../services/export/export.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { ClaimLabelComponent } from '../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../core/labels/tag.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { getExpandSubject } from '../../../functions/get-expand-subject';
import { findDiarySearchUpdate } from '../../../functions/todo/search-retain-updates';

@Component({
  selector: 'app-diary-table-top',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    ActionRowComponent,
    ButtonComponent,
    IconButtonComponent,
    CommonModule,
    ActionButtonComponent,
    MenuComponent,
    TagComponent,
    ReactiveFormsModule,
    ClaimLabelComponent,
    FormatClaimPipe,
  ],
  template: `
    @if (showActionButtons) {
      <app-action-row useMobileLayout>
        @if (isPageTodo) {
          <app-button right1 (click)="newDiary()" label="New diary" />
        } @else {
          <app-button right1 (click)="newClaimDiary()" label="New diary" />
        }
        <app-button
          right2
          emphasis="Low"
          (click)="editTypes()"
          label="Diary types"
        />
        @if (showPreferencesGear) {
          <app-icon-button
            right3
            [border]="false"
            ariaLabel="Shared diaries preferences"
            tooltip="Shared diaries preferences"
            icon="fa-light fa-gear"
            (click)="preferencesClick.emit()"
          />
        }
      </app-action-row>
    }

    @if (showTable) {
    <app-table
      [dataSource]="diaryEntries"
      [loading]="loading"
      [error]="error"
      [loadingHeight]="280"
      loadingStyle="Box"
      [pageSize]="10"
      [showSelection]="showSelection"
      [showExport]="true"
      [showActions]="showSelection"
      [actionsMenuExpanded]="actionMenuExpanded"
      [expandable]="true"
      [hideExpandAll]="false"
      dataKey="id"
      [scrollable]="false"
      (actionClick)="tableMenuShow($event, actionMenu)"
      (selectedEvent)="onSelection($event)"
      recordLabel="diary"
      recordLabelPlural="diaries"
      [showRecordLabel]="true"
      [showPageOfTotal]="showPageOfTotal"
      [recordLabelSuffix]="recordPeriodText"
      [ariaTableLabel]="'Diaries'"
      [rowExpandLabel]="getDiaryRowExpandLabel"
      (export)="onExport($event)"
      [globalFilterFields]="[
        'status',
        'case',
        'fileNumber',
        'formattedDueDate',
        'formattedNotificationDate',
        'firstName',
        'lastName',
        'message',
        'diaryType',
        'formattedUsers',
      ]"
    >
      <app-column name="status" label="Status">
        <ng-template let-data>
          <app-tag [type]="getTagStyle(data.status)">{{ data.status }}</app-tag>
        </ng-template>
      </app-column>
      <app-column name="case" label="Claim">
        <ng-template let-data>
          @if (data.recordSource && isPageTodo) {
            <app-claim-label
              [claim]="data"
              [showWatchButton]="!!data.fileNumber"
              [recordSource]="data.recordSource"
              [recordId]="data.navigatingRecID ?? data.recordId"
              [fileNumFormat]="data?.format"
            ></app-claim-label>
          } @else if (data.recordSource) {
            <span>{{
              (data.fileNumber || claim?.fileNumber || '')
                | formatClaim: (data.format || claim?.fileNumFormat)
                || data.case
                || '-'
            }}</span>
          } @else {
            <span>{{ data.fileNumber || '-' }}</span>
          }
        </ng-template>
      </app-column>
      <app-column name="fullName" label="Name" />
      <app-column name="dueDate" label="Due Date" format="d" />
      <app-column
        name="notificationDate"
        label="Notification Time"
        format="t"
      />
      <app-column name="formattedUsers" label="Follow up user">
        <ng-template let-data>
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="tw-max-w-full tw-whitespace-normal">
              {{ data.formattedUsers }}
            </div>
          </div>
        </ng-template>
      </app-column>
      <app-column name="diaryType" label="Diary Type" />
      <app-column name="actions" label="Actions" [sort]="false">
        <ng-template let-data>
          <app-action-button
            [menus]="[completeMenu, editMenu]"
            [row]="data"
            (click)="toggleActionMenu($event, data)"
          />
        </ng-template>
      </app-column>
      <ng-template #expand let-data>
        <div
          class="tw-whitespace-pre-line tw-py-2 tw-pr-2"
          role="region"
          [attr.aria-label]="'Message for diary ' + data.id"
        >
          <span class="tw-font-bold tw-pr-2">Message:</span>
          {{ data.message }}
        </div>
      </ng-template>
    </app-table>
    }
    <app-menu
      #actionMenu
      [items]="bulkActions"
      (click)="onBulkMenu($event)"
      (visibilityChange)="actionMenuExpanded = $event"
    />
    <app-menu
      [items]="completeActions"
      #completeMenu
      (click)="onMenu($event, completeMenu.data)"
    />
    <app-menu
      [items]="editActions"
      #editMenu
      (click)="onMenu($event, editMenu.data)"
    />
  `,
  styles: ``,
})
export class DiaryTableTopComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  protected getDiaryRowExpandLabel = (row: any) =>
    getExpandSubject(row?.fileNumber ?? row?.case, row?.diaryType) || 'Diary';

  @Output() markCompleteEvent = new EventEmitter<number>();
  @Output() markSelectedCompleteEvent = new EventEmitter<number[]>();
  @Output() markAllCompleteEvent = new EventEmitter<number[]>();
  @Output() preferencesClick = new EventEmitter<void>();

  @Input() showSelection = true;
  @Input() showActionButtons = false;
  @Input() showPreferencesGear = false;
  @Input() showTable = true;
  @Input() recordPeriodText = '';
  /** When true, the count is always "x of y diaries" with no date. */
  @Input() showPageOfTotal = false;
  @Input() diaryEntries?: any[] = [];
  @Input() isPageTodo = false;
  @Input() loading = false;
  @Input() error: any = null;

  @ViewChild('completeMenu') completeMenu!: MenuComponent;
  @ViewChild('editMenu') editMenu!: MenuComponent;
  @ViewChild('actionMenu') actionMenu!: MenuComponent;

  claim: any = null;
  selectedRows: any[] = [];
  actionMenuExpanded = false;

  bulkActions: Action[] = [
    { label: 'Mark selected complete', key: 'selected-complete', disabled: true },
    { label: 'Mark all complete', key: 'all-complete' },
  ];

  completeActions: Action[] = [
    { label: 'Mark complete', key: 'complete' },
    { label: 'Edit', key: 'edit' },
  ];

  editActions: Action[] = [{ label: 'Edit', key: 'edit' }];

  async ngOnInit(): Promise<void> {
    if (!this.isPageTodo) {
      this.claim = this.view.currentClaim();
    }
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateBulkActionBadges();
  }

  tableMenuShow(event: Event, menu: MenuComponent): void {
    this.updateBulkActionBadges();
    menu.toggle(event);
  }

  /** Same rule as the row Mark complete action: allow-others, or the current user created it. */
  private canMarkComplete(row: any): boolean {
    return (
      !!row?.allowOthers ||
      !!row?.createdBy?.includes(`(${this.session.user.userName})`)
    );
  }

  private completableIds(rows: any[] | null | undefined): number[] {
    return (rows ?? [])
      .filter((row) => this.canMarkComplete(row))
      .map((row) => row.id);
  }

  private updateBulkActionBadges(): void {
    const selected = this.completableIds(this.selectedRows).length;
    const all = this.completableIds(this.diaryEntries).length;
    const selectedAction = this.bulkActions.find(
      (a) => a.key === 'selected-complete',
    );
    const allAction = this.bulkActions.find((a) => a.key === 'all-complete');
    if (selectedAction) {
      selectedAction.badge = selected > 0 ? String(selected) : null;
      selectedAction.disabled = selected === 0;
    }
    if (allAction) {
      allAction.badge = all > 0 ? String(all) : null;
      allAction.disabled = false;
    }
  }

  onBulkMenu(action: Action): void {
    if (action.key === 'selected-complete') {
      this.markSelectedCompleteEvent.emit(
        this.completableIds(this.selectedRows),
      );
    } else if (action.key === 'all-complete') {
      this.markAllCompleteEvent.emit(this.completableIds(this.diaryEntries));
    }
  }

  onSelection(selections: any[]): void {
    this.selectedRows = selections ?? [];
    this.updateBulkActionBadges();
  }

  async onExport(format: ExportFormat): Promise<void> {
    try {
      const columns = [
        { propertyName: 'status', headerText: 'Status' },
        { propertyName: 'fileNumber', headerText: 'Claim' },
        { propertyName: 'firstName', headerText: 'First Name' },
        { propertyName: 'lastName', headerText: 'Last Name' },
        {
          propertyName: 'dueDate',
          headerText: 'Due Date',
          format: 'MM/dd/yyyy',
        },
        { propertyName: 'message', headerText: 'Description' },
        { propertyName: 'diaryType', headerText: 'Diary Type' },
        { propertyName: 'formattedUsers', headerText: 'Follow up user' },
        { propertyName: 'notificationDate', headerText: 'Notification Time' },
      ];
      const rows = this.diaryEntries;
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `Diary_${formattedDate}.${format}`;
      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Diary');
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'Diary',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (err) {
      this.error = err;
    }
  }

  getBackData() {
    return {
      backRouterLink: document.location.pathname,
      backLabel: 'My to-do',
    };
  }

  newDiary(): void {
    this.storage.set('current-diary', this.getBackData());
    this.router.navigate(['/diary-edit']);
  }

  newClaimDiary(): void {
    const data = {
      ...this.getBackData(),
      isClaimSpecific: true,
      claimUID: this.claim?.recordId,
      clientNumber: this.claim?.contractNumber,
      RecordSource: this.claim?.recordSource,
      fileNumber: this.claim?.fileNumber,
      ...(this.claim?.recordSource === 'T' && {
        employeeUID: this.claim?.employeeId,
      }),
    };
    this.storage.set('current-diary', data);
    this.router.navigate(['/diary-edit']);
  }

  editTypes(): void {
    const data = {
      ...this.getBackData(),
      ...(this.claim && {
        isClaimSpecific: true,
        clientNumber: this.claim.contractNumber,
      }),
    };
    this.storage.set('current-type', data);
    this.router.navigate(['/diary-types']);
  }

  onMenu(event: any, data: any): void {
    if (event.key === 'edit') {
      const saved = findDiarySearchUpdate(this.storage, data.id);
      const payload = {
        ...(saved ? { ...data, ...saved } : data),
        backRouterLink: document.location.pathname,
        backLabel: 'My to-do',
      };

      if (!this.isPageTodo && this.claim) {
        payload.isClaimSpecific = true;
        payload.claimUID = this.claim.recordId;
        payload.clientNumber = this.claim.contractNumber;
        payload.RecordSource = this.claim.recordSource;
        payload.fileNumber = this.claim.fileNumber;
        if (this.claim.recordSource === 'T') {
          payload.employeeUID = this.claim.employeeId;
        }
      }

      this.storage.set('current-diary', payload);
      this.router.navigate(['/diary-edit']);
    } else if (event.key === 'complete') {
      this.markCompleteEvent.emit(data.id);
    }
  }

  getTagStyle(status: string): TagType {
    switch (status) {
      case 'Open':
        return 'Info';
      case 'Past due':
        return 'Error';
      case 'Draft':
        return 'Warn';
      case 'Completed':
        return 'Success';
      default:
        return 'Success';
    }
  }

  toggleActionMenu(event: Event, data: any): void {
    if (this.canMarkComplete(data)) {
      this.completeMenu.toggle(event, data);
    } else {
      this.editMenu.toggle(event, data);
    }
  }
}
